import { Button, Form, Input, notification, Radio, Space } from 'antd'
import { useForm } from 'antd/lib/form/Form'
import { Branches, BranchRentees, Images } from 'gadjet-v2-types/dist/model'
import { useEffect, useRef, useState } from 'react'

import mask from '@utils/mask'

import BranchAPI from '@apis/branch'

import HiddenItems from '@components/atoms/Form/HiddenItems'
import UploadButton from '@components/molecules/UploadButton'

type Props = { hqId: number; branchId: number; branch?: Branches; onRefresh: () => void }

export default function BranchRenteeForm({ hqId, branchId, branch, onRefresh }: Props): JSX.Element {
  const [form] = useForm<any>() // Using any to allow article3ClauseEnabled field
  const [loading, setLoading] = useState(false)
  const hasUserChangedArticle3 = useRef(false)

  const onSubmit = async () => {
    const values = await form.validateFields()
    setLoading(true)

    // Separate article3ClauseEnabled from rentee data
    const { article3ClauseEnabled, ...renteeValues } = values

    // Update rentee information
    await BranchAPI.updateBranchRentee({ hqId, branchId }, { rentee: renteeValues })

    // If article3ClauseEnabled field exists, also update contractDocument
    if (article3ClauseEnabled !== undefined) {
      await BranchAPI.updateBranchContractDocument(
        { hqId, branchId },
        {
          contractDocument: {
            ...branch?.contractDocument,
            article3ClauseEnabled,
          },
        }
      )
    }

    setLoading(false)
    notification.success({ message: '저장되었습니다.' })
    hasUserChangedArticle3.current = false // Reset after successful save
    onRefresh()
  }

  const onUploadSignatureImage = ({ imageId }: Images) => form.setFieldsValue({ signatureImageId: imageId })

  const resetForm = () => {
    form.resetFields()
    if (branch?.rentee) {
      const formValues = {
        ...branch.rentee,
        // Only reset article3ClauseEnabled if user hasn't manually changed it
        ...(hasUserChangedArticle3.current
          ? {}
          : { article3ClauseEnabled: branch?.contractDocument?.article3ClauseEnabled ?? false }),
      }
      form.setFieldsValue(formValues)
    } else {
      // For new branches, set default article3ClauseEnabled
      form.setFieldsValue({
        article3ClauseEnabled: false,
      })
    }
  }

  useEffect(() => {
    resetForm()
  }, [branch])

  const initialValues: any = {
    name: '',
    businessNumber: '',
    corporationNumber: '',
    address: '',
    contact: '',
    article3ClauseEnabled: false,
  }

  return (
    <Form form={form} layout="vertical" initialValues={initialValues}>
      <HiddenItems names={['signatureImageId', 'consentFileId']} />

      <Form.Item label="임대인 이름" name="name">
        <Input />
      </Form.Item>
      <Form.Item label="임대인 사업자번호" name="businessNumber" {...mask.businessLicense}>
        <Input />
      </Form.Item>
      <Form.Item label="임대인 법인번호" name="corporationNumber" {...mask.personalLicense}>
        <Input />
      </Form.Item>
      <Form.Item label="임대인 주소" name="address">
        <Input />
      </Form.Item>
      <Form.Item label="임대인 연락처" name="contact" {...mask.contact}>
        <Input />
      </Form.Item>

      {/* Article 3 Additional Clause Configuration - Only show for rent contracts */}
      {branch?.contractDocument?.buildingContractType === 'rent' && (
        <Form.Item label="직권폐업 고지" name="article3ClauseEnabled">
          <Form.Item shouldUpdate noStyle>
            {({ getFieldValue }) => {
              const article3ClauseEnabled = getFieldValue('article3ClauseEnabled')
              return (
                <Radio.Group
                  value={article3ClauseEnabled ? 'true' : 'false'}
                  onChange={(e) => {
                    hasUserChangedArticle3.current = true // Mark that user has manually changed this field
                    // Manually set form field value to ensure proper binding
                    const booleanValue = e.target.value === 'true'
                    form.setFieldsValue({ article3ClauseEnabled: booleanValue })
                  }}
                >
                  <Space>
                    <Radio value="true">반영</Radio>
                    <Radio value="false">미반영</Radio>
                  </Space>
                </Radio.Group>
              )
            }}
          </Form.Item>
          <div style={{ marginTop: 8, fontSize: '12px', color: '#666', lineHeight: '1.4' }}>
            직권폐업 고지 반영 시 임대차 계약서의 [제 3조]에 다음의 추가 정보가 기재됩니다
            <br />
            ① 계약 연장을 하지 않을 경우 임차인은 사업장을 이전하거나 폐업을 하여야 한다.
            <br />② 이를 어길 시 세무서에서 직권폐업하거나 임대인이 이를 신청할 수 있다.
          </div>
        </Form.Item>
      )}

      <Form.Item shouldUpdate label="파일">
        {({ getFieldValue }) => {
          const signature = getFieldValue('signatureImage')
          return (
            <Space direction="vertical" style={{ width: '100%' }}>
              <UploadButton.Image
                category="signature"
                label="임대인 서명"
                accept="image/*"
                file={signature}
                onUploadDone={onUploadSignatureImage}
              />
            </Space>
          )
        }}
      </Form.Item>

      <Button type="primary" size="large" onClick={onSubmit} loading={loading}>
        저장하기
      </Button>
    </Form>
  )
}
